import axios from 'axios';
import { Alert, Col, Container, Table, Form, Card } from 'react-bootstrap';
import Spinner from '../components/Spinner';
import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import Pagination from '../components/Pagination';
import { FaDownload } from 'react-icons/fa';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

import '../styles/GRNList.css';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

export default function GRNList() {
  const { userData } = useContext(AuthContext);
  const company_code = userData?.company_code;
  const navigate = useNavigate();

  // GRN history logic
  const [grnHistory, setGrnHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // Per-column filters for GRN history table
  const [grnColumnFilter, setGrnColumnFilter] = useState({
    grn_id: '',
    po_number: '',
    supplier: '',
    received_date: '',
    status: '',
    batch_number: '',
    total_items: '',
    total_qty: '',
    submitted_by: ''
  });
    // Pagination
  const [grnCurrentPage, setGrnCurrentPage] = useState(1);
  const [grnItemsPerPage] = useState(15);

  useEffect(() => {
    const fetchHistory = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await axios.get(`${BASE_URL}/api/admin/grn/history`, {
          params: { company_code, po_type: 'direct' }
        });
        if (response.data.success) {
          setGrnHistory(response.data.grns || []);
        } else {
          setError(response.data.message || 'Failed to fetch GRN history');
        }
      } catch (err) {
        setError('Error fetching GRN history');
      } finally {
        setLoading(false);
      }
    };
    if (company_code) fetchHistory();
  }, [company_code]);

  // Helper to test if a value matches a filter (case-insensitive substring)
  const matches = (value, filter) => {
    if (!filter) return true;
    const val = (value === null || value === undefined) ? '' : String(value);
    return val.toLowerCase().includes(filter.toLowerCase());
  };

  // Reset page whenever filters or source data change
  useEffect(() => {
    setGrnCurrentPage(1);
  }, [grnColumnFilter, grnHistory]);

  // Compute filtered GRNs once
  const filteredGrns = grnHistory.filter(g => {
    if (!matches(g.grn_id, grnColumnFilter.grn_id)) return false;
    if (!matches(g.po_number, grnColumnFilter.po_number)) return false;
    if (!matches(g.supplier_name || g.supplier_id, grnColumnFilter.supplier)) return false;
    if (grnColumnFilter.received_date) {
      const grnIso = g.received_date ? new Date(g.received_date).toISOString().slice(0,10) : '';
      if (!grnIso.includes(grnColumnFilter.received_date)) return false;
    }
    if (grnColumnFilter.status) {
      if (((g.status || '').toLowerCase()) !== grnColumnFilter.status.toLowerCase()) return false;
    }
    if (!matches(g.batch_number || '-', grnColumnFilter.batch_number)) return false;
    if (!matches(g.total_items || g.items_count || '-', grnColumnFilter.total_items)) return false;
    if (!matches(g.total_qty || '-', grnColumnFilter.total_qty)) return false;
    if (!matches(g.email || g.submitted_by_name || g.warehouse_user_id || '-', grnColumnFilter.submitted_by)) return false;
    return true;
  });

  const grnTotalItems = filteredGrns.length;
  const grnTotalPages = Math.max(1, Math.ceil(grnTotalItems / grnItemsPerPage));
  const grnPage = Math.min(grnCurrentPage, grnTotalPages);
  const grnStartIndex = (grnPage - 1) * grnItemsPerPage;
  const grnDisplay = filteredGrns.slice(grnStartIndex, grnStartIndex + grnItemsPerPage);

  const handleDownload = async (e, grnId) => {
    e.stopPropagation();
    try {
      // Fetch company data for header
      const companyResp = await fetch(`${BASE_URL}/api/admin/grn/company-data?company_code=${company_code}`);
      const companyJson = await companyResp.json();
      let company = {};

      if (companyJson.success && companyJson.company) {
        company = companyJson.company;
      }

      // Fetch GRN details
      const resp = await fetch(`${BASE_URL}/api/admin/grn/grn-details/${grnId}?company_code=${company_code}`);
      const data = await resp.json();
      if (!data.success) throw new Error(data.message || 'Failed to fetch GRN details');

      // Fetch supplier data 
      const header = data.header || {};
      let supplier = {};
      if (header.supplier_id) {
        const supplierResp = await fetch(`${BASE_URL}/api/admin/grn/supplier-data?supplier_id=${header.supplier_id}&company_code=${company_code}`);
        const supplierJson = await supplierResp.json();
        if (supplierJson.success && supplierJson.supplier) {
          supplier = supplierJson.supplier;
        }
      }

      const doc = new jsPDF();
      let y = 20;
      const pageWidth = doc.internal.pageSize.getWidth();

      // Company Header
      if (company.company_name) {
        doc.setFontSize(18);
        doc.setFont("helvetica", "bold");
        doc.text(company.company_name.toString(), pageWidth / 2, y, { align: "center" });
        y += 8;
      }

      // Line separator
      doc.setDrawColor(150);
      doc.setLineWidth(0.5);
      doc.line(20, y, pageWidth - 20, y);
      y += 8;

      // Company contact info
      doc.setFontSize(10);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(100);
      
      // Build company address from separate fields
      if (company.address_line1 || company.address_line2 || company.city || company.state || company.postal_code || company.country) {
        const addressParts = [];
        if (company.address_line1) addressParts.push(company.address_line1);
        if (company.address_line2) addressParts.push(company.address_line2);
        if (company.city) addressParts.push(company.city);
        if (company.state) addressParts.push(company.state);
        if (company.postal_code) addressParts.push(company.postal_code);
        if (company.country) addressParts.push(company.country);
        const fullAddress = addressParts.join(', ');
        doc.text(fullAddress, pageWidth / 2, y, { align: "center" });
        y += 6;
      } else if (company.company_address) {
        // Fallback to single address field if available
        doc.text(company.company_address.toString(), pageWidth / 2, y, { align: "center" });
        y += 6;
      }
      
      if (company.company_email) {
        doc.text(company.company_email.toString(), pageWidth / 2, y, { align: "center" });
        y += 6;
      }
      if (company.company_phone) {
        doc.text(company.company_phone.toString(), pageWidth / 2, y, { align: "center" });
        y += 6;
      }
      doc.setTextColor(0);
      y += 1;

      //  GRN ID
      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.setFillColor(255, 255, 255);
      doc.rect(pageWidth / 2 - 30, y, 60, 10, "F");
      doc.text(`${header.grn_id?.toString() || grnId?.toString()}`, pageWidth / 2, y + 7, { align: "center" });
      y += 20;

      // PO Details (Left) 
      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.text("Purchase Order Details", 14, y);
      doc.setLineWidth(0.3);
      doc.line(14, y + 2, 80, y + 2);

      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.text(`PO Number: ${header.po_number?.toString() || '-'}`, 14, y + 10);
      doc.text(
        `Received Date: ${header.received_date ? new Date(header.received_date).toLocaleDateString() : '-'}`,
        14,
        y + 16
      );
      doc.text(`Status: ${header.status?.toString() || '-'}`, 14, y + 22);
      doc.text(`Batch Number: ${header.batch_number?.toString() || '-'}`, 14, y + 28);
      doc.text(`Invoice Number: ${header.invoice_number?.toString() || '-'}`, 14, y + 34);

      // Supplier Details (Right)
      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.text("Supplier Details", pageWidth - 14, y, { align: "right" });
      doc.line(pageWidth - 80, y + 2, pageWidth - 14, y + 2);

      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      if (supplier.supplier_name)
        doc.text(`${supplier.supplier_name.toString()}`, pageWidth - 14, y + 10, { align: "right" });
      if (supplier.address)
        doc.text(`${supplier.address.toString()}`, pageWidth - 14, y + 16, { align: "right" });
      if (supplier.email)
        doc.text(`${supplier.email.toString()}`, pageWidth - 14, y + 22, { align: "right" });
      if (supplier.phone)
        doc.text(`${supplier.phone.toString()}`, pageWidth - 14, y + 28, { align: "right" });

      // Table
      const tableColumn = ["Product Code", "SKU", "Ordered Qty", "Received Qty", "Lot Number", "Location"];
      const tableRows = (data.items || []).map(it => [
        it.style_number?.toString() || "-",
        it.sku?.toString() || "-",
        it.ordered_qty?.toString() ?? "-",
        it.received_qty?.toString() ?? "-",
        it.lot_no?.toString() || "-",
        it.location?.toString() || "-"
      ]);

      doc.autoTable({
        startY: y + 50,
        head: [tableColumn],
        body: tableRows,
        styles: { fontSize: 10, halign: "center", textColor: [0, 0, 0] },
        headStyles: {
          fillColor: [41, 128, 185],
          textColor: 255,
          fontStyle: "bold",
        },
        alternateRowStyles: { fillColor: [245, 245, 245] },
        tableLineColor: [237, 247, 255],
        tableLineWidth: 0.1,
      });

      // Footer
      const pageHeight = doc.internal.pageSize.getHeight();
      doc.setFontSize(9);
      doc.text(`Page ${doc.internal.getNumberOfPages().toString()}`, pageWidth - 20, pageHeight - 10, { align: "right" });

      // Save file
      doc.save(`${grnId?.toString()}.pdf`);
    } catch (err) {
      console.error("Error generating PDF", err);
      setError("Failed to generate PDF: " + (err.message || err));
    }
  };

  return (
    <Container fluid className='grn-list-page'>
        <div className='grn-list-section'>
            <Col>
                <div>
                    <Card.Body>
                    <div className="grn-list-header">
                        <h1>GRN History</h1>
                    </div>

                    {loading ? (
                        <div className="text-center py-5">
                        <Spinner text="Loading GRN history..." />
                        </div>
                    ) : error ? (
                        <Alert variant="danger">{error}</Alert>
                    ) : (
                        <div className="table-responsive">
                        <Table striped bordered hover responsive className='grn-history-table'>
                            <thead>
                            <tr>
                                <th>GRN ID</th>
                                <th>PO Number</th>
                                <th>Supplier</th>
                                <th>Received Date</th>
                                <th>Status</th>
                                <th>Batch Number</th>
                                <th>Total Items</th>
                                <th>Total Qty</th>
                                <th>Submitted By</th>
                                <th>Actions</th>
                            </tr>
                            </thead>
                            <tbody>
                            {/* Column-level search inputs */}
                            <tr className="table-filters-row">
                                <td>
                                <Form.Control size="sm" type="text" placeholder="Search GRN ID" value={grnColumnFilter.grn_id} onChange={e => setGrnColumnFilter({ ...grnColumnFilter, grn_id: e.target.value })} />
                                </td>
                                <td>
                                <Form.Control size="sm" type="text" placeholder="Search PO" value={grnColumnFilter.po_number} onChange={e => setGrnColumnFilter({ ...grnColumnFilter, po_number: e.target.value })} />
                                </td>
                                <td>
                                <Form.Control size="sm" type="text" placeholder="Search Supplier" value={grnColumnFilter.supplier} onChange={e => setGrnColumnFilter({ ...grnColumnFilter, supplier: e.target.value })} />
                                </td>
                                <td>
                                <Form.Control
                                    size="sm"
                                    type="date"
                                    placeholder="Received Date"
                                    value={grnColumnFilter.received_date}
                                    onChange={e => setGrnColumnFilter({ ...grnColumnFilter, received_date: e.target.value })}
                                />
                                </td>
                                <td>
                                <Form.Select size="sm" value={grnColumnFilter.status} onChange={e => setGrnColumnFilter({ ...grnColumnFilter, status: e.target.value })} className='grn-history-status-filter'>
                                    <option value="">All</option>
                                    <option value="pending">Pending</option>
                                    <option value="partial">Partial</option>
                                    <option value="completed">Completed</option>
                                </Form.Select>
                                </td>
                                <td>
                                <Form.Control size="sm" type="text" placeholder="Batch" value={grnColumnFilter.batch_number} onChange={e => setGrnColumnFilter({ ...grnColumnFilter, batch_number: e.target.value })} />
                                </td>
                                <td>
                                <Form.Control size="sm" type="text" placeholder="Items" value={grnColumnFilter.total_items} onChange={e => setGrnColumnFilter({ ...grnColumnFilter, total_items: e.target.value })} />
                                </td>
                                <td>
                                <Form.Control size="sm" type="text" placeholder="Qty" value={grnColumnFilter.total_qty} onChange={e => setGrnColumnFilter({ ...grnColumnFilter, total_qty: e.target.value })} />
                                </td>
                                <td>
                                <Form.Control size="sm" type="text" placeholder="Submitted By" value={grnColumnFilter.submitted_by} onChange={e => setGrnColumnFilter({ ...grnColumnFilter, submitted_by: e.target.value })} />
                                </td>
                                <td></td>
                            </tr>

                            {(() => {
                                if (grnTotalItems === 0) {
                                return <tr><td colSpan={9} className="text-center text-muted">No GRN records found</td></tr>;
                                }

                                return grnDisplay.map(grn => (
                                <tr key={grn.grn_id}>
                                    <td
                                      style={{ cursor: 'pointer', color: '#0d6efd', textDecoration: 'underline' }}
                                      onClick={() => navigate(`/warehouse/grn-details/${grn.grn_id}`)}
                                    >
                                      {grn.grn_id}
                                    </td>
                                    <td>{grn.po_number}</td>
                                    <td>{grn.supplier_name || grn.supplier_id}</td>
                                    <td>{grn.received_date ? new Date(grn.received_date).toLocaleDateString() : '-'}</td>
                                    <td>{grn.status}</td>
                                    <td>{grn.batch_number || '-'}</td>
                                    <td className='numbers-col'>{grn.total_items || grn.items_count || '-'}</td>
                                    <td className='numbers-col'>{grn.total_qty || '-'}</td>
                                    <td>{grn.email || grn.submitted_by_name || grn.warehouse_user_id || '-'}</td>
                                    <td>
                                      <FaDownload
                                        onClick={(e) => handleDownload(e, grn.grn_id)}
                                        className='download-grn-details'
                                        title="Download PDF"
                                      >
                                      </FaDownload>
                                    </td>
                                </tr>
                                ));
                            })()}
                            </tbody>
                        </Table>
                        {/* Pagination controls for GRN history */}
                        <Pagination
                            pagination={{
                            currentPage: grnPage,
                            totalPages: grnTotalPages,
                            totalItems: grnTotalItems,
                            itemsPerPage: grnItemsPerPage,
                            hasNextPage: grnPage < grnTotalPages,
                            hasPreviousPage: grnPage > 1
                            }}
                            onPageChange={setGrnCurrentPage}
                        />
                        </div>
                    )}
                    </Card.Body>
                </div>
            </Col>
        </div>
    </Container>
  );
}